import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import useDetailTable from '#/hooks/table/useDetailTable';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import {
  buildStockAdjustmentLineColumns,
} from '#/modules/imsMgmt/receipts/stockAdjustment/columns/stockAdjustmentLineColumns';

const EMPTY_SET = new Set();

const StockAdjustmentLinesTable = ({
  lines = [],
  status,
  canPrintLabel = false,
  selectedInventoryStockIds,
  selectAllChecked = false,
  selectAllDisabled = true,
  onToggleLine,
  onToggleAll,
}) => {
  const showCompletedFields = status === 'COMPLETED';
  const selectedIds = selectedInventoryStockIds ?? EMPTY_SET;
  const columns = useMemo(
    () => buildStockAdjustmentLineColumns({
      showCompletedFields,
      canPrintLabel,
      selectedInventoryStockIds: selectedIds,
      selectAllChecked,
      selectAllDisabled,
      onToggleLine,
      onToggleAll,
    }),
    [
      showCompletedFields,
      canPrintLabel,
      selectedIds,
      selectAllChecked,
      selectAllDisabled,
      onToggleLine,
      onToggleAll,
    ],
  );

  const rows = useMemo(
    () => (lines ?? []).map((line, index) => ({
      ...line,
      id: line.invStock?.id
        ? `${line.invStock.id}-${index}`
        : `line-${index}`,
    })),
    [lines],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  const getRowHighlight = useCallback((row) => {
    const stockId = row.original?.invStock?.id;
    return Boolean(stockId && selectedIds.has(stockId));
  }, [selectedIds]);

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
        getRowHighlight={canPrintLabel ? getRowHighlight : undefined}
      />
    </div>
  );
};

StockAdjustmentLinesTable.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
  status: PropTypes.string,
  canPrintLabel: PropTypes.bool,
  selectedInventoryStockIds: PropTypes.instanceOf(Set),
  selectAllChecked: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf(['indeterminate']),
  ]),
  selectAllDisabled: PropTypes.bool,
  onToggleLine: PropTypes.func,
  onToggleAll: PropTypes.func,
};

export default StockAdjustmentLinesTable;
